import { ComponentPropsWithoutRef, forwardRef } from "react"
import classNames from "classnames"
import { TextSize, Text } from ".."
import { devWarning } from "../utils/devWarning"
import { Sizes } from "@oysterhr/common"

type ProgressBarSize = Extract<Sizes, "xs" | "sm" | "md">

interface ProgressBarProps extends Omit<ComponentPropsWithoutRef<"div">, "children"> {
  /**
   * The literal value of the progress.
   * @default 0
   */
  value?: number
  /**
   * The maximum (allowable) literal value of the progress.
   * @default 100
   */
  max?: number
  /**
   * The color scheme of the progress indicator.
   * @default blue
   */
  variation?: "blue" | "green"
  /**
   * The size of the component.
   * @default sm
   */
  size?: ProgressBarSize
  /**
   * If `true`, the progress percent is displayed.
   * @default true
   */
  showProgressPercent?: boolean
  /**
   * The "aria-label" of the progress bar. This prop is highly recommended to make
   * the ProgressBar ARIA-compliant.
   */
  label?: string
}

const progressBarStylesMap: Record<ProgressBarSize, string> = {
  xs: "h-1 rounded-[4px]",
  sm: "h-2 rounded-[4px]",
  md: "h-3 rounded-lg",
}

const labelToTextSizeMap: Record<ProgressBarSize, TextSize> = {
  xs: "tiny",
  sm: "small",
  md: "medium",
}

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      value: valueProp = 0,
      max: maxProp = 100,
      variation = "blue",
      size = "sm",
      showProgressPercent = true,
      label,
      className: classNameProp,
      ...rest
    },
    ref,
  ) => {
    let value: number = valueProp
    let max: number = maxProp

    if (valueProp < 0) {
      devWarning(true, 'The ProgressBar "value" prop is less than 0. Setting "aria-valuenow" to 0.')
      value = 0
    }

    if (maxProp <= 0) {
      devWarning(true, 'The ProgressBar "max" prop is less than 1. Setting "aria-valuemax" to 1.')
      max = 1
    }

    if (valueProp > maxProp) {
      devWarning(
        true,
        'The ProgressBar "value" prop is greater than the "max" prop. Setting "aria-valuenow" to "aria-valuemax".',
      )
      value = max
    }

    const progressPercentText = Math.round((value / max) * 100) + "%"

    return (
      <div ref={ref} className={classNames(classNameProp, "flex items-center gap-x-3")} {...rest}>
        <div
          role="progressbar"
          aria-valuenow={value}
          aria-valuetext={progressPercentText}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-label={label}
          className={classNames("grow", progressBarStylesMap[size], "grid")}
        >
          <div
            className={classNames(
              "col-start-1 col-end-2 row-start-1 row-end-2 h-full w-full rounded-[inherit] bg-gray-200 ",
            )}
          ></div>
          <div
            style={{ width: `${progressPercentText}` }}
            className={classNames(
              "col-start-1 col-end-2 row-start-1 row-end-2 h-full rounded-[inherit] transition-[width]",
              variation === "blue" ? "bg-info-base" : "bg-success-lighten-50",
            )}
          ></div>
        </div>

        <Text
          size={labelToTextSizeMap[size]}
          className={classNames("font-medium", !showProgressPercent && "sr-only")}
        >
          {progressPercentText}
        </Text>
      </div>
    )
  },
)

ProgressBar.displayName = "ProgressBar"
